import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';
import {
    Body,
    Box,
    BoxProps,
    BrowseStepper,
    Button,
    ChakraGrid,
    ContactCard,
    Flex,
    Heading,
    Hero,
    HtmlContent,
    IconMail,
    ImgProps,
    LightTable,
    LinkCard,
    ListDivider,
    MediaModal,
    MediaSlider,
    Meta,
    PersonCard,
    Stack,
    Tag,
    TextButton,
    TheaterModal,
    Tile,
    useDisclosure,
    useTheaterModal,
    VStack,
    Wrapper,
} from 'designsystem';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    fetchApiData,
    gtm,
    isLoggedIn,
    LoadingFilmPage,
    slugifyTitle,
    useApi,
    useFilmInfo,
    useGetApiImageProps,
    VideoButtons,
} from 'shared';
import {
    ForumFilmDocument,
    ForumFilmQuery,
    ForumFilmQueryVariables,
    ForumFilterEnum,
    useForumFilmQuery,
    useForumFilmStepperQuery,
} from '../../../gql/api';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import PageSection from '../../../components/PageSection';
import { getProjectDetailsLines, getProjectInformationLines, joinTranslations } from '../../../utils/forumUtils';
import { ForumFilterQueryParams } from '../../../components/pages/ForumCollectionPage';
import { NumberParam, useQueryParam, useQueryParams } from 'use-query-params';
import useBrowseStepper, { getStepperQueryVars, StepperData } from '../../../hooks/useBrowseStepper';
import { NextSeo } from 'next-seo';
import striptags from 'striptags';
import { DEFAULT_REVALIDATION_TIME } from '../../[...uri]';

interface ProjectSpecificationItemProps {
    title: string;
    value: ReactNode;
}

const ProjectSpecificationItem: FC<ProjectSpecificationItemProps> = ({ title, value }) => (
    <Box>
        <Body fontWeight="600" mb={3}>
            {title}
        </Body>
        {value}
    </Box>
);

interface ViewMoreButtonProps {
    isOpen: boolean;
    onToggle: () => void;
}

const ViewMoreButton: FC<ViewMoreButtonProps> = ({ isOpen, onToggle }) => (
    <TextButton onClick={onToggle} iconRight={null}>
        <Body color="darkblue.60">
            <FormattedMessage
                defaultMessage="View {collapsed} info"
                values={{
                    collapsed: isOpen ? (
                        <FormattedMessage defaultMessage="less" />
                    ) : (
                        <FormattedMessage defaultMessage="all" />
                    ),
                }}
            />
        </Body>
    </TextButton>
);

const ForumDetail: FC = () => {
    const { status: sessionStatus } = useSession();
    const { query, asPath, isFallback } = useRouter();
    const vars = { id: query.id as string };
    const { data, isLoading } = useQuery(
        [...useForumFilmQuery.getKey(vars), { loggedIn: sessionStatus === 'authenticated' }],
        useApi<ForumFilmQuery, ForumFilmQueryVariables>(ForumFilmDocument).bind(null, vars)
    );
    const film = data?.film;
    const meetingRequest = data?.meetingRequest;
    const { isOpen: isBiographyOpen, onToggle: toggleBiography } = useDisclosure();
    const { isOpen: isSynopsisOpen, onToggle: toggleSynopsis } = useDisclosure();
    const { isOpen: isProjectDescriptionOpen, onToggle: toggleProjectDescription } = useDisclosure();
    const { formatMessage } = useIntl();
    const directors = useMemo(() => film?.credits?.filter(credit => credit.role.key === 'director'), [film?.credits]);
    const pitchTeam = useMemo(() => film?.credits?.filter(credit => credit.role.key === 'pitchteam'), [film?.credits]);

    const loggedIn = isLoggedIn(sessionStatus);
    const showMeetingRequestTile =
        sessionStatus !== 'loading' && (!loggedIn || (loggedIn && meetingRequest?.meetingRequestLink));
    const projectInformationLines = getProjectInformationLines(film);
    const projectDetailsLines = getProjectDetailsLines(film);

    const [filterParams] = useQueryParams(ForumFilterQueryParams);
    const [offset] = useQueryParam('offset', NumberParam);
    const { data: stepperData, isLoading: isStepperLoading } = useForumFilmStepperQuery(
        getStepperQueryVars<ForumFilterEnum>(filterParams, offset, query.query as string),
        { enabled: offset >= 0 }
    );
    const { nextFilm, current, total, onNext, onPrev } = useBrowseStepper(
        stepperData?.searchForum as StepperData,
        ForumFilterQueryParams
    );

    const browseStepper = (
        <BrowseStepper
            title={<FormattedMessage defaultMessage="Explore the collection" />}
            body={
                <FormattedMessage
                    defaultMessage="{current, number}/{total, number} {total, plural, one {result} other {results}}"
                    values={{ current, total }}
                />
            }
            isLoading={isStepperLoading}
            current={offset + 1}
            total={total}
            prevDisabled={offset === 0}
            nextDisabled={!nextFilm}
            onPrev={onPrev}
            onNext={onNext}
        />
    );

    const { videoSrc, handleVideoClick, ...disclosureProps } = useTheaterModal();

    const { locale } = useRouter();

    useEffect(() => {
        if (film) {
            gtm.pageView(asPath, {
                page_pillar: 'professionals',
                page_language: locale,
                film_id: film.id,
                film_name: film.fullPreferredTitle,
                film_edition: film.edition.year.toString(),
                film_section: film.sections?.join(', ') ?? '',
                film_subject:
                    film.subject?.map(s => s.translation).join(', ') ??
                    film.tags?.map(tag => tag.translation).join(', ') ??
                    '',
            });
        }
    }, [film, asPath, locale]);
    const {
        title,
        description,
        mediaVideos,
        mediaImages,
        heroImage,
        films,
        trailers,
        ogImage,
        edition,
        mostRecentEdition,
        credits,
        mediaModalProps,
    } = useFilmInfo({
        film,
        filmOrProjectType: 'forum',
    });
    const allMedia = [...mediaVideos, ...mediaImages];
    const getImageProps = useGetApiImageProps();

    if (isFallback || isLoading) {
        return <LoadingFilmPage />;
    }

    return (
        film && (
            <>
                <NextSeo
                    title={`${title} (${edition.year})`}
                    description={striptags(description)}
                    openGraph={{
                        images: ogImage,
                    }}
                    // the most recent film is the 'canonical' film to prevent a film from getting duplicated in search results.
                    canonical={
                        mostRecentEdition &&
                        `${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/forum/${mostRecentEdition.id}/${slugifyTitle(
                            film.fullPreferredTitle
                        )}`
                    }
                    titleTemplate="%s | IDFA Forum"
                />
                <Hero
                    image={heroImage}
                    variant="dark"
                    w="100%"
                    rightChildren={
                        showMeetingRequestTile && (
                            <Tile colorScheme="professionals" gridRow="2">
                                <Flex flexDirection="column">
                                    {!loggedIn && (
                                        <Box px={6}>
                                            <Heading variant={2} mb={4}>
                                                <FormattedMessage defaultMessage="IDFA Forum Project Catalogue" />
                                            </Heading>
                                            <Body mb={6}>
                                                <FormattedMessage defaultMessage="IDFA Forum accredited guests can log in for more detailed project information." />
                                            </Body>
                                            <Flex justifyContent="space-between">
                                                <Link href={process.env.NEXT_PUBLIC_MYIDFA_LOGIN || ''}>
                                                    <Button px={11} py={3}>
                                                        <FormattedMessage defaultMessage="Log in" />
                                                    </Button>
                                                </Link>

                                                <TextButton as={Link} href="/learn-more">
                                                    <Body>
                                                        <FormattedMessage defaultMessage="Learn more" />
                                                    </Body>
                                                </TextButton>
                                            </Flex>
                                        </Box>
                                    )}
                                </Flex>
                            </Tile>
                        )
                    }
                >
                    <Flex flexDirection="column">
                        <Stack spacing={[4, null, 6]}>
                            <Tag size="l">
                                <Body fontSize={[4, null, 5]}>IDFA Forum</Body>
                            </Tag>
                            <Stack spacing={4}>
                                <Heading variant={1} clamp={2} mb={[2, null, 4]}>
                                    {film.fullPreferredTitle}
                                </Heading>
                                <Box mb={[6, null, 11]}>
                                    <Meta
                                        items={[
                                            {
                                                key: 'director',
                                                value: directors.map(director => director.fullName).join(', '),
                                            },
                                            {
                                                key: 'countries-of-production',
                                                value: joinTranslations(film.countriesOfProduction),
                                            },
                                        ]}
                                        size="m"
                                    />
                                </Box>
                            </Stack>
                            <VideoButtons films={films} trailers={trailers} handleVideoClick={handleVideoClick} />
                        </Stack>
                    </Flex>
                </Hero>
                <TheaterModal
                    title={film.fullPreferredTitle}
                    meta={{
                        items: [
                            {
                                key: 'director',
                                value: directors.map(director => director.fullName).join(', '),
                            },
                            {
                                key: 'countries-of-production',
                                value: joinTranslations(film.countriesOfProduction),
                            },
                        ],
                    }}
                    videoSrc={videoSrc}
                    {...disclosureProps}
                />
                <Flex flexDir="column" position="relative" borderBottom="1px solid" borderColor="neutral.30">
                    <Box as="section" py={[6, null, 8]}>
                        <Wrapper>
                            <Stack direction={['column', null, 'row']} spacing={[6, null, 9]} divider={<ListDivider />}>
                                {film.edition?.name && (
                                    <ProjectSpecificationItem
                                        title={formatMessage({
                                            defaultMessage: 'Selected for',
                                        })}
                                        value={<Tag>{film.edition.name}</Tag>}
                                    />
                                )}
                                {film.requestType && (
                                    <ProjectSpecificationItem
                                        title={formatMessage({
                                            defaultMessage: 'Presentation category',
                                        })}
                                        value={
                                            <Body>
                                                {joinTranslations(
                                                    film.requestType.sort((a, b) => a.sortOrder - b.sortOrder)
                                                )}
                                            </Body>
                                        }
                                    />
                                )}
                                {film.productionStatus && (
                                    <ProjectSpecificationItem
                                        title={formatMessage({
                                            defaultMessage: 'Production status',
                                        })}
                                        value={
                                            <Body>
                                                {joinTranslations(
                                                    film.productionStatus.sort((a, b) => a.sortOrder - b.sortOrder)
                                                )}
                                            </Body>
                                        }
                                    />
                                )}
                            </Stack>
                        </Wrapper>
                    </Box>
                    <Box
                        display={['none', null, null, null, 'block']}
                        position="absolute"
                        top={0}
                        right={0}
                        minH="144px"
                        py={8}
                        px="84px"
                        bgColor="neutral.10"
                    >
                        {browseStepper}
                    </Box>
                </Flex>
                <VStack direction="column" py={[9, null, 11]} spacing={[9, null, 11]}>
                    {film.contactPerson?.email && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Contact" />
                            </Heading>
                            <ContactCard
                                name={film.contactPerson.fullName}
                                icon={
                                    <Box w={6} h={6} mt={1} as="a" href={`mailto:${film.contactPerson.email}`}>
                                        <IconMail />
                                    </Box>
                                }
                            />
                        </PageSection>
                    )}
                    {film.logline && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Logline" />
                            </Heading>
                            <Body>{film.logline}</Body>
                        </PageSection>
                    )}
                    <Box
                        display={['flex', null, null, null, 'none']}
                        bg="neutral.10"
                        h="100%"
                        w="100%"
                        justifyContent="center"
                        py={9}
                    >
                        {browseStepper}
                    </Box>
                    {projectInformationLines.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Project information" />
                            </Heading>
                            <LightTable lines={projectInformationLines} spacing={4} />
                        </PageSection>
                    )}
                    {loggedIn && projectDetailsLines.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Project details" />
                            </Heading>
                            <LightTable lines={projectDetailsLines} spacing={4} />
                        </PageSection>
                    )}

                    {film.forumSynopsisEntry && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Synopsis" />
                            </Heading>
                            <Box>
                                <Body clamp={isSynopsisOpen ? 0 : 4} mb={4}>
                                    <HtmlContent html={film.forumSynopsisEntry.translation} />
                                </Body>
                                <ViewMoreButton onToggle={toggleSynopsis} isOpen={isSynopsisOpen} />
                            </Box>
                        </PageSection>
                    )}
                    {loggedIn && allMedia?.length > 0 && (
                        <Stack as="section" spacing={6} w="100%">
                            <Wrapper>
                                <Heading variant={3} as="h2">
                                    <FormattedMessage defaultMessage="Media" />
                                </Heading>
                            </Wrapper>
                            <MediaSlider media={[...mediaImages, ...mediaVideos]} />
                        </Stack>
                    )}
                    {film.forumProjectDescriptionEntry && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Project description" />
                            </Heading>
                            <Box>
                                <Body clamp={isProjectDescriptionOpen ? 0 : 4} mb={4}>
                                    <HtmlContent html={film.forumProjectDescriptionEntry.translation} />
                                </Body>
                                <ViewMoreButton onToggle={toggleProjectDescription} isOpen={isProjectDescriptionOpen} />
                            </Box>
                        </PageSection>
                    )}
                    {loggedIn && pitchTeam?.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Pitch Team" />
                            </Heading>
                            <ChakraGrid gridTemplateColumns={['1fr 1fr', null, '1fr 1fr 1fr']} gridRowGap={6}>
                                {pitchTeam.map((pitcher, i) => (
                                    <PersonCard
                                        key={pitcher.forumPerson?.id ?? `pitcher-${i}`}
                                        name={pitcher.fullName}
                                        photo={
                                            getImageProps(
                                                pitcher.forumPerson?.mainAccreditation?.publications?.favoriteImage,
                                                pitcher.fullName
                                            ) as ImgProps & BoxProps
                                        }
                                        jobDescription={pitcher.role.translation}
                                    />
                                ))}
                            </ChakraGrid>
                        </PageSection>
                    )}
                    {film.forumBiography && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Biography" />
                            </Heading>
                            <Box>
                                <Body clamp={isBiographyOpen ? 0 : 4} mb={4}>
                                    <HtmlContent html={film.forumBiography.translation} />
                                </Body>
                                <ViewMoreButton onToggle={toggleBiography} isOpen={isBiographyOpen} />
                            </Box>
                        </PageSection>
                    )}
                    {film.credits?.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Credits" />
                            </Heading>
                            <LightTable lines={credits} />
                        </PageSection>
                    )}
                    {film.publications?.links?.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Additional Material" />
                            </Heading>
                            <VStack spacing={[2, null, 4]} w="100%">
                                {film.publications.links.map(link => (
                                    <Box key={link.value} w="100%">
                                        <Link href={link.value} target="_blank">
                                            <LinkCard>{link.title}</LinkCard>
                                        </Link>
                                    </Box>
                                ))}
                            </VStack>
                        </PageSection>
                    )}
                </VStack>
                <MediaModal
                    {...mediaModalProps}
                    closeLabel={<FormattedMessage defaultMessage="Close" description="Close modal" />}
                />
            </>
        )
    );
};
export const getStaticPaths: GetStaticPaths = async () => ({
    // we don't provide paths because we won't fetch any films during build time.
    paths: [],

    fallback: true,
});

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const queryClient = new QueryClient();
    const variables = {
        id: params?.id as string,
    };
    const data = await queryClient.fetchQuery([...useForumFilmQuery.getKey(variables), { loggedIn: false }], () =>
        fetchApiData<ForumFilmQuery, ForumFilmQueryVariables>({
            locale,
            query: useForumFilmQuery.document,
            variables,
        })
    );

    if (!data.film) {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default ForumDetail;
